import { AppTooltip } from '@/components/base/AppTooltip';
import { HintButton as Button } from '@/components/base/HintButton';
import { GlobalSettingsButton } from '@/components/settings/GlobalSettingsButton';
import { lazy, Suspense, useId, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Classes, Icon } from '@blueprintjs/core';
import logo from '../../assets/digitiles-logo.svg';
import { discoveryLayout } from '../../styled-system/recipes';

const AccountPanel = lazy(() =>
  import('@/account/AccountPanel').then((module) => ({ default: module.AccountPanel })),
);
export const discoveryStyles = discoveryLayout();
const navigationPreference = 'digitiles.discovery.navigation-collapsed.v1';

function readCollapsed() {
  try {
    const saved = sessionStorage.getItem(navigationPreference);
    if (saved !== null) return saved === 'true';
  } catch {
    // Navigation remains usable when browser storage is unavailable.
  }
  return window.matchMedia?.('(max-width: 700px)').matches ?? false;
}

export function DiscoveryLayout({
  children,
  accountAction,
}: {
  children: ReactNode;
  accountAction?: ReactNode;
}) {
  const s = discoveryStyles;
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const navigationId = useId();
  const toggleNavigation = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      sessionStorage.setItem(navigationPreference, String(next));
    } catch {
      // Keep the in-memory preference for this page.
    }
  };
  return (
    <div
      className={`${Classes.DARK} ${s.page}`}
      data-navigation-collapsed={collapsed}
      data-discovery-scroll
    >
      <a href="#discovery-content" className="skip-link">
        Skip to content
      </a>
      <aside className={s.nav} aria-label="Workspace navigation">
        <div className={s.navInner}>
          <div className={s.railHeader} data-rail-heading>
            <NavLink to="/" className={s.brand} aria-label="DigiTiles home">
              <img src={logo} alt="" />
              <span data-rail-label>DigiTiles</span>
            </NavLink>
            <Button
              variant="minimal"
              icon={collapsed ? 'chevron-right' : 'chevron-left'}
              aria-label={
                collapsed
                  ? 'Expand workspace navigation'
                  : 'Collapse workspace navigation'
              }
              title={
                collapsed
                  ? 'Expand workspace navigation'
                  : 'Collapse workspace navigation'
              }
              aria-expanded={!collapsed}
              aria-controls={navigationId}
              onClick={toggleNavigation}
            />
          </div>
          <nav
            id={navigationId}
            className={s.navLinks}
            aria-label="Main navigation"
            data-rail-navigation
          >
            <AppTooltip
              content="Projects"
              disabled={!collapsed}
              renderTarget={({ ref, isOpen: _isOpen, ...hintProps }) => (
                <NavLink {...hintProps} ref={ref} to="/" end aria-label="Projects">
                  <Icon icon="folder-close" aria-hidden="true" />
                  <span data-rail-label>Projects</span>
                </NavLink>
              )}
            />
            <AppTooltip
              content="Challenges"
              disabled={!collapsed}
              renderTarget={({ ref, isOpen: _isOpen, ...hintProps }) => (
                <NavLink
                  {...hintProps}
                  ref={ref}
                  to="/challenges"
                  aria-label="Challenges"
                >
                  <Icon icon="list" aria-hidden="true" />
                  <span data-rail-label>Challenges</span>
                </NavLink>
              )}
            />
            <AppTooltip
              content="Tutorials in editor"
              disabled={!collapsed}
              renderTarget={({ ref, isOpen: _isOpen, ...hintProps }) => (
                <a
                  {...hintProps}
                  ref={ref}
                  href="/editor?lesson=workspace-tools"
                  className={s.editorLink}
                  aria-label="Tutorials in editor"
                >
                  <Icon icon="help" aria-hidden="true" />
                  <span data-rail-label>Tutorials in editor</span>
                </a>
              )}
            />
            <AppTooltip
              content="Discord (opens in a new tab)"
              disabled={!collapsed}
              renderTarget={({ isOpen: _isOpen, ...targetProps }) => (
                <a
                  {...targetProps}
                  href="https://discord.gg/jGPFCERZdT"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Discord (opens in a new tab)"
                >
                  <Icon icon="chat" aria-hidden="true" />
                  <span data-rail-label>Discord</span>
                </a>
              )}
            />
          </nav>
        </div>
      </aside>
      <main id="discovery-content" tabIndex={-1} className={s.content}>
        <div className={s.accountActions}>
          <GlobalSettingsButton />
          <Suspense
            fallback={
              <Button icon="user" disabled style={{ width: 164, maxWidth: '70vw' }}>
                Login
              </Button>
            }
          >
            {accountAction ?? <AccountPanel />}
          </Suspense>
        </div>
        {children}
      </main>
    </div>
  );
}
